import { useRouter } from "next/router";
import { useState } from "react";
import DropdownMenu, { DropdownEntry } from "@/components/ui/DropdownMenu";
import Dialog from "@/components/ui/Dialog";
import Button from "@/components/ui/Button";
import axios from "axios";
import { Icon } from "@iconify/react";
import Image from "next/image";
import SideTooltip from "./SideTooltip";

import { logout } from "@/services/auth";
import { changelogPageUrl } from "@/services/changelog.service";
import { User } from "@/types/user";
import { formatEmail } from "@/utils/text";

interface ProfilePageProps {
  user: User | null;
  setUser: (user: User | null) => void;
  isCollapsed?: boolean;
  onClose?: () => void;
}

const ProfilePage = ({ user, setUser, isCollapsed, onClose }: ProfilePageProps) => {
  const router = useRouter();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const confirmLogout = async () => {
    setLoading(true);
    try {
      await logout();
      setUser(null); // Clear context
      router.push("/login");
    } catch (err) {
      if (axios.isAxiosError(err)) {
        console.error("Logout failed:", err.response?.data || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutTrigger = () => {
    setShowLogoutConfirm(true);
  };

  const initials = user?.name
    ?.split(" ")
    .map(n => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const displayEmail = user?.email ? formatEmail(user.email) : "";

  const profileTrigger = (
    <div
      className={`select-none flex items-center ${isCollapsed ? "justify-center py-1" : "gap-3 px-3 py-1.5"} rounded-control transition-colors duration-150 hover:bg-black/[0.04] dark:hover:bg-white/[0.06]`}
    >
      {user?.profile_picture_url ? (
        <Image
          src={user.profile_picture_url}
          alt=""
          width={64}
          height={64}
          className="h-8 w-8 shrink-0 rounded-full object-cover"
        />
      ) : (
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-700 dark:bg-white/10 dark:text-gray-200"
          aria-hidden="true"
        >
          {initials || <Icon icon="lucide:user" className="text-base" />}
        </div>
      )}
      {!isCollapsed && (
        <div className="min-w-0 flex-1 text-left leading-tight">
          <p className="truncate text-sm font-medium text-fg">{user?.name}</p>
          <p className="truncate text-xs text-fg-muted" title={user?.email}>
            {displayEmail}
          </p>
        </div>
      )}
    </div>
  );

  // Mirrors Firecrawl's grouped account dropdown (account links / resources
  // / sign out, divided) but only with sections Spidra actually has --
  // skips Firecrawl's Domain checker, Team, and "Join our community" since
  // none of those exist here, rather than adding dead links for parity.
  const profileItems: DropdownEntry[] = [
    ...(user?.role === "admin"
      ? [
          { label: "Admin", icon: "lucide:shield", href: "/admin", onClick: onClose },
          { divider: true } as const,
        ]
      : []),
    { label: "Settings", icon: "lucide:settings", href: "/dashboard/settings", onClick: onClose },
    { label: "Billing", icon: "lucide:credit-card", href: "/dashboard/billing", onClick: onClose },
    { label: "Usage", icon: "lucide:chart-line", href: "/dashboard/usage", onClick: onClose },
    { label: "API keys", icon: "lucide:key-round", href: "/dashboard/api-keys", onClick: onClose },
    { divider: true },
    { label: "Changelog", icon: "lucide:newspaper", href: changelogPageUrl, external: true, onClick: onClose },
    { label: "Documentation", icon: "lucide:book-open", href: "https://docs.spidra.io/", external: true, onClick: onClose },
    { divider: true },
    {
      label: "Log out",
      icon: "lucide:log-out",
      color: "text-danger-text",
      onClick: () => {
        handleLogoutTrigger();
        if (onClose) onClose();
      },
    },
  ];

  return (
    <>
      <SideTooltip label={user?.name ?? "Account"} enabled={!!isCollapsed}>
        <DropdownMenu
          trigger={profileTrigger}
          items={profileItems}
          position="top-left"
          label={`Account menu for ${user?.name ?? "your account"}`}
        />
      </SideTooltip>
      <Dialog
        isOpen={showLogoutConfirm}
        onClose={() => !loading && setShowLogoutConfirm(false)}
        title="Log out"
        width="sm"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-fg-secondary">
            Are you sure you want to log out?
          </p>
          <div className="mt-2 flex items-center justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowLogoutConfirm(false)} disabled={loading}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmLogout} loading={loading} loadingText="Logging out...">
              Log out
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ProfilePage;
